import { useRef, useState } from 'react';
import FormModal from '@/shared/components/form-modal';
import { MaterialFormContainer, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import {
    darkPanelStyle,
    darkTextDim,
    darkDangerText,
    darkSuccessText,
    darkWarningText,
    errorBannerStyle,
    warningBannerStyle,
} from '@/shared/dark-inline-styles';
import { CourseCrmImportReport } from '@/models/course-crm';
import { useImportCourseCrm } from '@/services/course-crm';

interface Props {
    isOpen: boolean;
    toggle: () => void;
}

const ACTION_COLOR: Record<string, string> = {
    created: darkSuccessText,
    updated: '#7da3ff',
    skipped: darkWarningText,
    error: darkDangerText,
};

/**
 * The one-time spreadsheet migration.
 *
 * DRY RUN FIRST, ALWAYS: the admin previews the whole report — every row, every
 * change, every refusal — and only then gets a Commit button. A migration whose
 * first action is a write is not a migration tool, and this one touches real
 * customer records across two collections.
 */
const ImportModal = ({ isOpen, toggle }: Props) => {
    const [csv, setCsv] = useState('');
    const [fileName, setFileName] = useState('');
    const [createMissingUsers, setCreateMissingUsers] = useState(false);
    const [report, setReport] = useState<CourseCrmImportReport | null>(null);
    const [committed, setCommitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const { mutateAsync: runImport, isPending } = useImportCourseCrm();

    const reset = () => {
        setCsv('');
        setFileName('');
        setReport(null);
        setCommitted(false);
        setError(null);
        setCreateMissingUsers(false);
    };

    const onFile = async (file?: File) => {
        if (!file) return;
        setFileName(file.name);
        setReport(null);
        setCommitted(false);
        setCsv(await file.text());
    };

    const run = async (dryRun: boolean) => {
        setError(null);
        if (!csv.trim()) {
            setError('Choose a CSV file, or paste its contents below.');
            return;
        }
        const result = await runImport({ csv, dryRun, createMissingUsers });
        if (result.status === 'error') {
            setError(result.error?.message ?? 'The import failed.');
            return;
        }
        setReport(result.data ?? null);
        setCommitted(!dryRun);
    };

    const close = () => {
        reset();
        toggle();
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={false}
            loading={isPending}
            title="Import clients from the spreadsheet"
            toggle={close}
            handleDelete={() => undefined}
            // The primary button previews; committing is a second, deliberate press.
            handleSave={() => (report && !committed ? run(false) : run(true))}
            body={
                <MaterialFormContainer onSubmit={() => run(true)}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    <div style={{ ...darkPanelStyle, textAlign: 'left', marginBottom: 12 }}>
                        <p style={{ margin: 0, fontSize: 12.5, color: darkTextDim }}>
                            Export the sheet as <strong>CSV</strong> and choose it here. The first row must be the header; the usual column names
                            (Customer Name, Type, TG ID, Email, Phone, Address, Referral, Joined TG Group, Delivery Status, Cohort, Testimonial
                            Video/Post, Notes) are recognised automatically, and anything unrecognised is reported rather than rejected.
                        </p>
                    </div>

                    <div style={{ textAlign: 'left', marginBottom: 12 }}>
                        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
                        {fileName && <span style={{ marginLeft: 10, fontSize: 12.5, color: darkTextDim }}>{fileName}</span>}
                    </div>

                    <MaterialTextField
                        label="…or paste the CSV here"
                        multiline
                        value={csv}
                        onChange={(e) => {
                            setCsv(e.target.value);
                            setReport(null);
                            setCommitted(false);
                        }}
                    />

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0', textAlign: 'left', fontSize: 13 }}>
                        <input type="checkbox" checked={createMissingUsers} onChange={(e) => setCreateMissingUsers(e.target.checked)} />
                        Create a Harmonea account for any email that has none
                    </label>
                    <p style={{ margin: '-4px 0 12px', fontSize: 12, color: darkTextDim, textAlign: 'left' }}>
                        Leave this off for the first pass. A row with no email address can never be imported — Firebase Auth needs one — and those
                        rows are listed by name so you can chase them by hand.
                    </p>

                    {report && (
                        <div style={{ textAlign: 'left' }}>
                            <div style={committed ? { ...warningBannerStyle, color: darkSuccessText } : warningBannerStyle}>
                                <strong>{committed ? 'Imported.' : 'Preview only — nothing has been written.'}</strong> {report.totalRows} rows ·{' '}
                                {report.created} to create · {report.updated} to update · {report.skipped} skipped · {report.errors} errors
                                {!committed && <div style={{ marginTop: 6 }}>Press Save again to commit.</div>}
                            </div>

                            <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12, color: darkTextDim }}>
                                {report.notes.map((n) => (
                                    <li key={n}>{n}</li>
                                ))}
                            </ul>

                            {report.unknownHeaders.length > 0 && (
                                <p style={{ fontSize: 12.5, color: darkWarningText }}>Columns ignored: {report.unknownHeaders.join(', ')}</p>
                            )}
                            {report.duplicateEmails.length > 0 && (
                                <p style={{ fontSize: 12.5, color: darkDangerText }}>
                                    Repeated email addresses (only the first row of each is imported):{' '}
                                    {report.duplicateEmails.map((d) => `${d.email} (rows ${d.rows.join(', ')})`).join('; ')}
                                </p>
                            )}
                            {report.duplicatePhones.length > 0 && (
                                <p style={{ fontSize: 12.5, color: darkWarningText }}>
                                    Repeated phone numbers — same household, or a copy/paste slip:{' '}
                                    {report.duplicatePhones.map((d) => `rows ${d.rows.join(', ')}`).join('; ')}
                                </p>
                            )}

                            <div style={{ ...darkPanelStyle, maxHeight: 280, overflowY: 'auto' }}>
                                {report.rows.map((r) => (
                                    <div key={r.rowNumber} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                        <span style={{ fontSize: 13 }}>
                                            <span style={{ color: darkTextDim }}>Row {r.rowNumber}</span> · {r.name ?? '—'}{' '}
                                            <span style={{ color: ACTION_COLOR[r.action] ?? darkTextDim, fontWeight: 600 }}>{r.action}</span>
                                        </span>
                                        {r.changes.length > 0 && (
                                            <span style={{ display: 'block', fontSize: 11.5, color: darkTextDim }}>{r.changes.join(' · ')}</span>
                                        )}
                                        {r.warnings.map((w) => (
                                            <span key={w} style={{ display: 'block', fontSize: 11.5, color: darkWarningText }}>
                                                ⚠ {w}
                                            </span>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </MaterialFormContainer>
            }
        />
    );
};

export default ImportModal;
